import * as React from 'react';
import { XIcon } from 'lucide-react';
import { cn } from '../lib/utils';
import { Badge } from './ui/badge';

export interface TagInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  /** Characters that commit the current text as a tag, besides Enter. */
  separators?: string[];
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-invalid'?: boolean;
}

/**
 * Free-text tags: type and press Enter (or a separator) to add, Backspace on an
 * empty box to remove the last. The replacement for antd's
 * `<Select mode="tags">`. Duplicates are ignored, case-insensitively.
 */
export const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(function TagInput(
  { value, onChange, placeholder, separators = [','], disabled, className, id, ...aria },
  ref,
) {
  const [text, setText] = React.useState('');

  const commit = (raw: string) => {
    const parts = raw
      .split(new RegExp(`[${separators.map((s) => `\\${s}`).join('')}]`))
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length === 0) return;

    const seen = new Set(value.map((tag) => tag.toLowerCase()));
    const next = [...value];
    for (const part of parts) {
      if (!seen.has(part.toLowerCase())) {
        seen.add(part.toLowerCase());
        next.push(part);
      }
    }
    onChange(next);
    setText('');
  };

  return (
    <div
      className={cn(
        'flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-input bg-card px-2 py-1 shadow-xs transition-[color,box-shadow]',
        'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
        aria['aria-invalid'] && 'border-destructive',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
    >
      {value.map((tag) => (
        <Badge key={tag} variant="secondary" className="gap-0.5 pe-1 text-xs font-medium">
          {tag}
          <button
            type="button"
            aria-label={`Remove ${tag}`}
            className="rounded-sm opacity-60 hover:opacity-100"
            onClick={() => onChange(value.filter((item) => item !== tag))}
          >
            <XIcon />
          </button>
        </Badge>
      ))}
      <input
        ref={ref}
        id={id}
        value={text}
        disabled={disabled}
        placeholder={value.length === 0 ? placeholder : undefined}
        className="h-7 min-w-24 flex-1 bg-transparent px-1 text-sm outline-none placeholder:text-muted-foreground"
        onChange={(event) => {
          const next = event.target.value;
          if (separators.some((separator) => next.includes(separator))) commit(next);
          else setText(next);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit(text);
          } else if (event.key === 'Backspace' && text === '' && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => commit(text)}
      />
    </div>
  );
});
